import { useEffect, useRef } from 'react';
import { API } from '../api/api';

// Opens an SSE connection scoped to whichever employee's calendar is
// currently being viewed, so a change made by the owner or a delegate shows
// up for other viewers immediately instead of waiting for the server's
// timelog cache to expire. Re-subscribes automatically when the viewed
// employee changes (e.g. switching delegates), and EventSource reconnects on
// its own if the connection drops.
//
// Besides the plain 'timelogs-changed' (refetch everything), the server can push one
// log ('timelog-upsert' with { log }) or a removal ('timelog-removed' with { id }),
// which is how a Service Tool write shows up in a second or two without a refetch.
export function useTimelogEventStream({ authenticated, employeeId, onChange, onUpsert, onRemove }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onUpsertRef = useRef(onUpsert);
  onUpsertRef.current = onUpsert;
  const onRemoveRef = useRef(onRemove);
  onRemoveRef.current = onRemove;

  useEffect(() => {
    if (!authenticated || !employeeId) return;

    const source = new EventSource(API.timelogStreamUrl(employeeId), { withCredentials: true });
    source.addEventListener('timelogs-changed', () => onChangeRef.current());
    const parse = (e) => { try { return JSON.parse(e.data); } catch { return null; } };
    source.addEventListener('timelog-upsert', (e) => {
      const data = parse(e);
      if (data?.log) onUpsertRef.current?.(data.log); else onChangeRef.current();
    });
    source.addEventListener('timelog-removed', (e) => {
      const data = parse(e);
      if (data?.id) onRemoveRef.current?.(data.id); else onChangeRef.current();
    });

    return () => source.close();
  }, [authenticated, employeeId]);
}
